import { Amplify } from 'aws-amplify';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import { createBrowserHistory, LocationState } from 'history';
import { ModalProvider } from 'mui-modal-provider';
import { SnackbarProvider } from 'notistack';
import React from 'react';
import { Router } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import DateUtils from '@date-io/dayjs';
import { createMuiTheme, MuiThemeProvider, StylesProvider } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider } from '@material-ui/pickers/MuiPickersUtilsProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import awsconfigAdmin from './aws-exports-admin';
import awsconfigVenue from './aws-exports-venue';
import { Auth } from './components/Auth';
import { Layout } from './components/Layout/Layout';
import { env } from './consts';
import AppDataContextProvider from './providers/AppDataContext';
import { AppStateContextProvider } from './providers/AppStateContext';
import { Routes } from './Routes';
import { defaultTheme } from './themes/default.theme';

dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(weekOfYear);

const awsconfig =
  (process.env.REACT_APP_WEBSITE || 'admin') === 'admin' ? awsconfigAdmin : awsconfigVenue;

const config = (awsconfig as Record<string, any>)[env];
Amplify.configure(config);

const queryClient = new QueryClient();

// Force every Select/dropdown menu to open below its field instead of
// overlapping it (MUI v4 anchors to the selected item by default).
const muiTheme = createMuiTheme({
  props: {
    MuiSelect: {
      MenuProps: {
        getContentAnchorEl: null,
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        transformOrigin: { vertical: 'top', horizontal: 'left' },
      },
    },
  },
});

const history = createBrowserHistory<LocationState>({ basename: '/' });

export const App: React.FC = () => (
  <StylesProvider injectFirst>
    <MuiThemeProvider theme={muiTheme}>
      <MuiPickersUtilsProvider utils={DateUtils}>
      <AppStateContextProvider>
        <Auth>
          <ThemeProvider theme={defaultTheme}>
            <SnackbarProvider
              maxSnack={3}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              <QueryClientProvider client={queryClient}>
                <AppDataContextProvider>
                  <Router history={history}>
                    <ModalProvider legacy>
                      <Layout>{Routes}</Layout>
                    </ModalProvider>
                  </Router>
                </AppDataContextProvider>
              </QueryClientProvider>
            </SnackbarProvider>
          </ThemeProvider>
        </Auth>
      </AppStateContextProvider>
      </MuiPickersUtilsProvider>
    </MuiThemeProvider>
  </StylesProvider>
);
